import React from 'react';
import PropTypes from 'prop-types';
import { LocaleService } from '@sm360/widget-commons-core-services';
import { Quote, CurrencyFormats, PurchaseMethods } from '@sm360/widget-commons-vehicle';

import * as PaymentFrequencies from 'commons-vehicle/PurchaseMethods/constants/paymentFrequencies.js';

const { PurchaseMethodsService } = PurchaseMethods;

const GarageFinance = ({ quote }) => {
    const { finance, purchaseMethod } = quote;
    const { term, paymentFrequency, incentives, tradeIn, tradeInTaxCreditsDetails = {} } = finance;
    const { apr, totalPayment, payment, paymentTaxes } = term;

    const getPaymentFrequencyLabels = (I18n) => {
        const paymentFrequencyLabels = {
            periodically: '',
            perPeriod: '',
        };

        const selectedFinanceTerm = paymentFrequency;
        if (!selectedFinanceTerm) {
            return paymentFrequencyLabels;
        }

        switch (selectedFinanceTerm) {
            case PaymentFrequencies.WEEKLY:
                paymentFrequencyLabels.periodically = I18n.t('paymentFrequencies.weekly');
                paymentFrequencyLabels.perPeriod = I18n.t('paymentFrequencies.perWeek');
                break;
            case PaymentFrequencies.BI_WEEKLY:
                paymentFrequencyLabels.periodically = I18n.t('paymentFrequencies.biWeekly');
                paymentFrequencyLabels.perPeriod = I18n.t('paymentFrequencies.perBiWeek');
                break;
            case PaymentFrequencies.SEMI_MONTHLY:
                paymentFrequencyLabels.periodically = I18n.t('paymentFrequencies.semiMonthly');
                paymentFrequencyLabels.perPeriod = I18n.t('paymentFrequencies.perSemiMonth');
                break;
            case PaymentFrequencies.MONTHLY:
                paymentFrequencyLabels.periodically = I18n.t('paymentFrequencies.monthly');
                paymentFrequencyLabels.perPeriod = I18n.t('paymentFrequencies.perMonth');
                break;
            default:
                break;
        }

        return paymentFrequencyLabels;
    };

    const getPurchaseMethodLabels = (I18n) => {
        const purchaseMethodLabels = {
            name: I18n.t('purchaseMethods.cashPurchase'),
            verb: I18n.t('purchaseMethods.cashPurchaseVerb'),
        };

        if (purchaseMethod === 'lease') {
            purchaseMethodLabels.name = I18n.t('purchaseMethods.lease');
            purchaseMethodLabels.verb = I18n.t('purchaseMethods.leaseVerb');
        } else if (purchaseMethod === 'finance') {
            purchaseMethodLabels.name = I18n.t('purchaseMethods.finance');
            purchaseMethodLabels.verb = I18n.t('purchaseMethods.financeVerb');
        }

        return purchaseMethodLabels;
    };

    const purchaseLabels = getPurchaseMethodLabels(LocaleService);

    const paymentLabels = getPaymentFrequencyLabels(LocaleService);
    const rebates = incentives.total;
    const calculatedFormattedRebates = rebates > 0 ? LocaleService.l(rebates, CurrencyFormats.CURRENCY_2) : '';

    const months = `${term.term} ${LocaleService.t('purchaseMethods.monthsAbr')}`;

    const cashDown = LocaleService.l(finance.cashDown, CurrencyFormats.CURRENCY_2);

    const calculatedFormattedPaymentTaxes = `${LocaleService.l(paymentTaxes, CurrencyFormats.CURRENCY_2)}`;
    const calculatedFormattedTotalPayment = `${LocaleService.l(totalPayment, CurrencyFormats.CURRENCY_2)}`;

    const calculatedPaymentSplit = PurchaseMethodsService.getFormattedCurrencySplit(
        LocaleService.l(payment, CurrencyFormats.CURRENCY_2)
    );

    const paymentWithTaxes = `+ ${calculatedFormattedPaymentTaxes} = ${calculatedFormattedTotalPayment}`;

    const formattedCashDown = LocaleService.t('DOWN_PAYMENTS.CASH_DOWN_WITH_AMOUNT', { cashDown });
    const tradeInAmount = LocaleService.l(tradeIn, CurrencyFormats.CURRENCY_2);
    const tradeInWithTaxCredits = LocaleService.l(tradeInTaxCreditsDetails.totalTradeIn, CurrencyFormats.CURRENCY_2);
    const taxCreditsAmount = LocaleService.l(tradeInTaxCreditsDetails.tradeInTaxCredits, CurrencyFormats.CURRENCY_2);

    return (
        <div className="payment-wrapper">
            <p className="payment-title sr-text is-bold">{purchaseLabels.verb}</p>
            <div className="payment-info">
                <p className="payment-price">
                    <span className="is-price sr-text is-bold">
                        {calculatedPaymentSplit.beforeDecimal}
                        <sup>{calculatedPaymentSplit.afterDecimal}</sup> &nbsp;
                        {paymentLabels.perPeriod}
                    </span>{' '}
                    &nbsp;
                    {paymentWithTaxes}
                </p>
                <p className="payment-summary">
                    <span>
                        {apr.toFixed(2).split('.')[0]}
                        <sup>{apr.toFixed(2).split('.')[1]}%</sup>
                    </span>{' '}
                    &nbsp;
                    {months}
                </p>
                <p className="payment-account">{formattedCashDown}</p>
            </div>
            {rebates > 0 && (
                <div className="vehicle-rebate">
                    <icon className="icon-promo2 is-price" />
                    <p>
                        {LocaleService.t('GARAGE.VEHICLE_STATUS.BONUS_REBATE', { rebate: calculatedFormattedRebates })}
                    </p>
                </div>
            )}
            {tradeIn > 0 && (
                <div className="vehicle-rebate">
                    <icon className="icon-refresh is-util" />
                    <p>
                        {LocaleService.t('GARAGE.VEHICLE_STATUS.TRADE_IN', {
                            tradeInAmount,
                            taxCreditsAmount,
                            tradeInWithTaxCredits,
                        })}
                    </p>
                </div>
            )}
        </div>
    );
};

GarageFinance.propTypes = { quote: PropTypes.instanceOf(Quote.QuoteModelV25).isRequired };

GarageFinance.displayName = 'GarageFinance';

export default GarageFinance;
